/**
 * @file useSkuModeItems.ts
 * @description Режим «одна фасовка» диалога печати: партии с остатком,
 *   выбор партии и строка печати по выбранной партии (phase19.22, ui-design §5).
 *
 * В других режимах хук ничего не делает и отдаёт строки как есть — диалогу
 * не нужно ветвиться. Партия по умолчанию — первая в списке сервера (FEFO:
 * без срока — первыми); партий нет — «без партии» с предупреждением.
 * Склад печати — склад выбранной партии: строка остатка = склад × партия.
 */

import { useEffect, useMemo, useState } from "react";

import type { LabelPrintDialogItem, LabelPrintMode } from "./label-print-types";
import { rememberSkuTemplate } from "./useSkuLabelTemplates";
import { labelsApi, type SkuLabelLotDto } from "../../../lib/api-labels";
import { defaultLabelQty } from "../../../lib/label-sheets";

/** Выбор партии: id партии или «без партии». */
export const NO_LOT = "none";

interface Params {
  open: boolean;
  mode: LabelPrintMode;
  items: LabelPrintDialogItem[];
  skuLots?: SkuLabelLotDto[];
  warehouseId: string;
}

export function useSkuModeItems({ open, mode, items, skuLots, warehouseId }: Params) {
  const isSku = mode === "sku";
  const base = items[0];
  const baseSkuId = base?.skuId;
  const [loaded, setLoaded] = useState<SkuLabelLotDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [choice, setChoice] = useState<string>(NO_LOT);
  const lots = useMemo(() => skuLots ?? loaded ?? [], [skuLots, loaded]);

  // Партии не переданы — загружаем сами (право просмотра склада).
  useEffect(() => {
    if (!open || !isSku || skuLots || !baseSkuId) return;
    let cancelled = false;
    setLoading(true);
    labelsApi
      .skuLots(baseSkuId)
      .then((res) => {
        if (cancelled) return;
        setLoaded(res.lots);
        rememberSkuTemplate(baseSkuId, res.sku.effectiveTemplate);
      })
      .catch(() => {
        if (!cancelled) setLoaded([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isSku, skuLots, baseSkuId]);

  // Открытие / новый набор партий — первая по FEFO, иначе «без партии».
  // Ключ — id партий, а не массив: вызывающий может собирать его заново.
  const firstLotId = lots[0]?.lotId ?? NO_LOT;
  const lotsKey = lots.map((l) => l.lotId).join(",");
  useEffect(() => {
    if (open && isSku) setChoice(firstLotId);
  }, [open, isSku, lotsKey, firstLotId]);

  const lot = lots.find((l) => l.lotId === choice) ?? null;

  const effectiveItems = useMemo<LabelPrintDialogItem[]>(() => {
    if (!isSku || !base) return items;
    if (!lot) return [{ ...base, lotId: null, lotCode: undefined, batchNumber: undefined, expiresAt: null }];
    return [
      {
        ...base,
        lotId: lot.lotId,
        lotCode: lot.code,
        batchNumber: lot.batchNumber,
        expiresAt: lot.expiresAt,
        labelStatus: lot.labelStatus,
        defaultQty: defaultLabelQty({ quantity: lot.quantity, isMeasured: base.isMeasured }),
      },
    ];
  }, [isSku, base, items, lot]);

  return {
    isSku,
    lots,
    loading,
    choice,
    setChoice,
    items: effectiveItems,
    /** Смена партии пересобирает форму (количество по умолчанию — остаток партии). */
    resetKey: isSku
      ? `sku:${choice}`
      : items.map((i) => `${i.receiptItemId ?? i.lotId ?? i.skuId}:${i.defaultQty}`).join("|"),
    warehouseId: lot?.warehouseId ?? warehouseId,
  };
}
